/**
 * Bill Validation Dashboard (docs/superpowers/plans/2026-10-27-bv-dashboard-rework.md).
 *
 * The Dashboard reads one month and computes nothing Bill Runs does not: it is
 * built from the same rows (buildBillRunRows) and metrics (computeMetrics), and
 * every figure links into Bill Runs with the matching filter, so the two pages
 * cannot disagree. This module holds the few figures only the Dashboard shows.
 */
import { STAGE_ORDER, type StageKey } from './billPipeline';
import type { BillRunMetrics } from './billRunMetrics';
import type { BillRunRow } from './billRunRows';
import { NO_RUN_CONDITIONS } from './billRunConditions';
import { billRunStatus } from './billRunStatus';
import type { BillWorkflowStatus } from './billWorkflow';
import { siteValue } from './siteFilters';
import type { BvSiteStatus } from '../types/billValidationTypes';
import { EMPTY_VIEW, writeBillRunsView, type BillRunsView } from './billRunsUrl';

/** The Bill Runs URL for `month` (Period end mode, as the Dashboard counts) plus `patch`. */
export function runsLink(month: string, patch: Partial<Omit<BillRunsView, 'conditions'>> & { conditions?: Partial<BillRunsView['conditions']> } = {}): string {
  const view: BillRunsView = {
    ...EMPTY_VIEW, from: month, to: month, mode: 'end', ...patch,
    conditions: { ...NO_RUN_CONDITIONS, ...patch.conditions },
  };
  return `/bill-validation/runs?${writeBillRunsView(view).toString()}`;
}

export type FlowStep = { key: 'bills' | 'parsed' | 'usage' | 'priced' | 'validated'; label: string; count: number };

/** Bills past `stage`: their first unpassed stage is a later one, or they are done. */
const past = (current: StageKey | 'done', stage: StageKey) =>
  current === 'done' || STAGE_ORDER.indexOf(current) > STAGE_ORDER.indexOf(stage);

/**
 * How far the month's bills have got from invoice to report, each bill once.
 * Each step counts the bills that passed it, so the drop between two steps is
 * where bills are waiting: on usage data, on network/TOU pricing, or the run.
 */
export function usageFlow(rows: readonly BillRunRow[]): FlowStep[] {
  const seen = new Set<string>();
  const n = { bills: 0, parsed: 0, usage: 0, priced: 0, validated: 0 };
  for (const r of rows) {
    if (r.kind !== 'bill' || r.archived || seen.has(r.board.bill.id)) continue;
    seen.add(r.board.bill.id);
    const { current } = r.board.pipeline;
    n.bills += 1;
    if (past(current, 'nmi')) n.parsed += 1;
    if (past(current, 'usage')) n.usage += 1;
    if (past(current, 'tou')) n.priced += 1;
    if (current === 'done') n.validated += 1;
  }
  return [
    { key: 'bills', label: 'Bills received', count: n.bills },
    { key: 'parsed', label: 'Invoice parsed', count: n.parsed },
    { key: 'usage', label: 'Usage data applied', count: n.usage },
    { key: 'priced', label: 'Network & TOU priced', count: n.priced },
    { key: 'validated', label: 'Validated', count: n.validated },
  ];
}

export type Headline = {
  expected: number;
  hasBill: number;
  missing: number;
  /** Bills with a verdict: matched + mismatched. */
  validated: number;
  /** In-scope bills with no verdict yet (Outcome's Awaiting validation). */
  awaiting: number;
  matched: number;
  mismatched: number;
  /** Mismatched bills charged more than expected. */
  overcharged: number;
  /** Mismatched bills charged less than expected. */
  undercharged: number;
  /** Reports (send status other than No report). */
  reports: number;
  /** Every bill, the Reports sent tile's base: sent + notSent + sendFailed + changedSinceSent + noReport. */
  bills: number;
  /** Bills with no report yet: not validated. */
  noReport: number;
  sent: number;
  /** Reports ready but never sent: sending is a person's step (Bill Runs → Reports). */
  notSent: number;
  sendFailed: number;
  /** Sent, then the bill's totals changed. */
  changedSinceSent: number;
};

const pct = (n: number, of: number) => (of > 0 ? Math.round((n / of) * 1000) / 10 : null);
export { pct as percentOf };

export function headline(m: BillRunMetrics): Headline {
  const o = m.outcome;
  const validated = o.matched + o.overcharge + o.undercharge + o.mismatchPending;
  const reports = m.send.not_sent + m.send.sent + m.send.send_failed + m.send.changed_since_sent;
  return {
    expected: m.coverage.total.expected,
    hasBill: m.coverage.total.hasBill,
    missing: m.coverage.total.missing,
    validated,
    awaiting: o.awaitingValidation,
    matched: o.matched,
    mismatched: validated - o.matched,
    overcharged: o.overcharge,
    undercharged: o.undercharge,
    reports,
    bills: reports + m.send.no_report,
    noReport: m.send.no_report,
    sent: m.send.sent,
    notSent: m.send.not_sent,
    sendFailed: m.send.send_failed,
    changedSinceSent: m.send.changed_since_sent,
  };
}

/**
 * The Bills received tile's sub line. No Inbox count: most unmatched invoices
 * are for NMIs that are not BV sites, so it does not explain the gap.
 */
export function billsReceivedSub(h: Pick<Headline, 'hasBill' | 'expected' | 'missing'>): string {
  const p = pct(h.hasBill, h.expected);
  return `${p == null ? '—' : `${p}%`} of enrolled sites · ${h.missing} no bill yet`;
}

/** The Reports sent tile's sub line: every bill not sent, in non-zero parts. */
export function reportsSub(h: Pick<Headline, 'bills' | 'notSent' | 'sendFailed' | 'changedSinceSent' | 'noReport'>): string {
  if (!h.bills) return 'No bills';
  return [
    h.notSent > 0 && `${h.notSent} ready, not sent`,
    h.sendFailed > 0 && `${h.sendFailed} failed to send`,
    h.changedSinceSent > 0 && `${h.changedSinceSent} changed since sent`,
    h.noReport > 0 && `${h.noReport} not validated yet`,
  ].filter(Boolean).join(' · ') || 'All sent';
}

/**
 * The Validated tile's sub line: one management figure. Every bill not yet
 * validated is "awaiting bill run" — whether it waits on parsing, usage data
 * or the run itself is operational detail (the Jobs page).
 */
// Awaiting is the Outcome count, not bills − validated: a bill on a site not
// enrolled during its period is never validated and must not read as awaiting.
export function validatedSub(h: Pick<Headline, 'bills' | 'awaiting'>): string {
  if (!h.bills) return 'No bills';
  return h.awaiting > 0 ? `${h.awaiting} awaiting bill run` : 'All validated';
}

/** The statuses that wait on a person, in the order they are worked. */
export const NEEDS_A_PERSON: { status: BillWorkflowStatus; label: string; hint: string }[] = [
  { status: 'mismatched', label: 'Mismatched', hint: 'Check, then raise with the retailer or hold for review' },
  { status: 'internal_review', label: 'Needs Internal Review', hint: 'Analysts to check the figures' },
  { status: 'system_review', label: 'Needs System Review', hint: 'Build team: calculation, parsing or config' },
  { status: 'follow_up_retailer', label: 'Follow up with Retailer', hint: 'Waiting on the retailer' },
  { status: 'ready_to_send', label: 'Ready to Send', hint: 'Report ready for the customer' },
  { status: 'waiting_customer', label: 'Waiting on Customer', hint: 'Sent, waiting on a reply' },
];

// ── Sites: onboarded and enrolled (not month-bound) ─────────────────────────

/**
 * The Enrolled tile counts BV sites whose stored bill_val_status is this.
 * Decision D-1 (docs/superpowers/plans/2026-10-28-bv-nta-reports.md): Choice
 * Energy asked for "enrolled, active, receiving DMAs" and may redefine it —
 * change this one constant if they do. Onboarded is every BV site
 * (isBvSite: bv_extn not empty), whatever its status.
 */
export const ENROLLED_SITE_STATUS: BvSiteStatus = 'Enrolled';

export type SiteCounts = { onboarded: number; enrolled: number };

// ── Over/under by client ────────────────────────────────────────────────────

export type ClientTotals = {
  /** The site's client (entity) name; '' when the site has none. */
  entity: string;
  /** Bills ending in the month, archived left out. */
  bills: number;
  /** Bills with a verdict: matched + mismatched, as the Validated tile. */
  validated: number;
  overcharged: number;
  /** Sum of the overcharged bills' differences, positive. */
  overchargedDollars: number;
  undercharged: number;
  /** Sum of the undercharged bills' differences, as a positive amount. */
  underchargedDollars: number;
  /** overchargedDollars + underchargedDollars: how far the client's bills were off. */
  differenceDollars: number;
  /** Reports sent (send status Sent), as the Reports sent tile. */
  sent: number;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

const emptyTotals = (entity: string): ClientTotals => ({
  entity, bills: 0, validated: 0, overcharged: 0, overchargedDollars: 0,
  undercharged: 0, underchargedDollars: 0, differenceDollars: 0, sent: 0,
});

function addTotals(t: ClientTotals, o: ClientTotals): void {
  t.bills += o.bills;
  t.validated += o.validated;
  t.overcharged += o.overcharged;
  t.overchargedDollars = round2(t.overchargedDollars + o.overchargedDollars);
  t.undercharged += o.undercharged;
  t.underchargedDollars = round2(t.underchargedDollars + o.underchargedDollars);
  t.differenceDollars = round2(t.overchargedDollars + t.underchargedDollars);
  t.sent += o.sent;
}

/**
 * The month's bills per client: each bill once, archived bills left out. The
 * verdicts are the rows' own (billRunStatus over the board, built with the
 * Dashboard's tolerance), so the counts add up to the Validated, Mismatched and
 * Reports sent tiles. The dollars are each bill's report difference
 * (row.figures.variance). Sorted by total difference, largest first.
 */
export function clientTotals(rows: readonly BillRunRow[]): ClientTotals[] {
  const seen = new Set<string>();
  const byEntity = new Map<string, ClientTotals>();
  for (const r of rows) {
    if (r.kind !== 'bill' || r.archived || seen.has(r.board.bill.id)) continue;
    seen.add(r.board.bill.id);
    const entity = siteValue(r.board.bill, 'entity');
    const t = byEntity.get(entity) ?? emptyTotals(entity);
    byEntity.set(entity, t);
    const s = billRunStatus(r);
    const variance = r.figures?.variance ?? 0;
    const one = emptyTotals(entity);
    one.bills = 1;
    if (s.send === 'sent') one.sent = 1;
    // Verdicts only for bills in the service (row.inScope), as the Validated and
    // Mismatched tiles; bills and sent count every bill, as Reports sent.
    if (r.inScope) {
      if (s.status === 'matched' || s.status === 'mismatched') one.validated = 1;
      if (s.details === 'overcharge') { one.overcharged = 1; one.overchargedDollars = Math.abs(variance); }
      if (s.details === 'undercharge') { one.undercharged = 1; one.underchargedDollars = Math.abs(variance); }
    }
    addTotals(t, one);
  }
  return [...byEntity.values()].sort((a, b) =>
    b.differenceDollars - a.differenceDollars
    || (b.overcharged + b.undercharged) - (a.overcharged + a.undercharged)
    || a.entity.localeCompare(b.entity));
}

/** Every client's totals added up: the table's Total row. */
export function sumClientTotals(list: readonly ClientTotals[]): ClientTotals {
  const total = emptyTotals('All clients');
  for (const t of list) addTotals(total, t);
  return total;
}

